export function calculate(answers) {
  // Extract inputs (assumed to be provided via sliders)
  const niYears = Number(answers.niYears);
  const purchaseYears = Number(answers.purchaseYears);
  const retirementYears = Number(answers.retirementYears); // default value

  // Validate total qualifying years
  const totalYears = niYears + purchaseYears;
  if (totalYears > 35) {
    return {
      status: "Limit Exceeded",
      items: [],
      message: "The maximum number of required years on your National Insurance record to qualify for the full UK State Pension is 35. Please review your inputs.",
      color: "orange",
      title: "Lifetime Earnings"
    };
  }
  if (totalYears < 10) {
    return {
      status: "Minimum Required",
      items: [],
      message: "The number of required years on your National Insurance record to qualify for the minimum UK State Pension is 10. You will need to purchase more years to generate results.",
      color: "orange",
      title: "Lifetime Earnings"
    };
  }

  // Constants from CSV/developer notes
  const class2Cost = 179.40;       // cost per qualifying year for Class 2
  const class3Cost = 907.40;       // cost per qualifying year for Class 3
  const annualPension = 328.64;    // annual value per qualifying year
  const conversionRate = 1.2024992; // conversion factor
  
  // Step 3: Calculate A7 (cost using Class 2 contributions)
  const A7 = (purchaseYears * class2Cost) * conversionRate;
  // Step 4: Calculate A8 (cost using Class 3 contributions)
  const A8 = (purchaseYears * class3Cost) * conversionRate;
  // Step 5: Calculate A9 (additional annual pension from purchased years)
  const A9 = (purchaseYears * annualPension) * conversionRate;
  // Step 6: Calculate A10 (total annual pension entitlement)
  const A10 = ((niYears + purchaseYears) * annualPension) * conversionRate;
  // Step 7: Calculate A11 (lifetime pension over retirement period)
  const A11 = A10 * retirementYears;
  // Step 8: Calculate ROI for Class 2 and Class 3
  const ROI_Class2 = purchaseYears ? ((A11 - A7) / A7) * 100 : 0;
  const ROI_Class3 = purchaseYears ? ((A11 - A8) / A8) * 100 : 0;

  // Format numbers with commas for thousands and fixed two decimals
  const format = num => Number(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Assemble display items
  const items = [];
  items.push(" ");

  // Create table data with Name, Value, and Explanation
const table = [
    { 
      name: `Cost of purchasing ${purchaseYears} qualifying years using Class 2 voluntary contributions`, 
      value: `€${format(A7)}`, 
      explanation: (
        <span>
          Class 2 contributions are significantly cheaper than Class 3, but they are harder to qualify for.<br/><br/>
          <a 
            href="https://nationalpensionhelpline.ie/pensions/uk-state-pension-buyback-guide-from-ireland/#contribution-classes" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ color: '#0645AD', textDecoration: 'underline' }}
          >
            Learn more about “Contribution Classes” and how to qualify for each.
          </a>
        </span>
      )
    },
    { 
      name: `Cost of purchasing ${purchaseYears} qualifying years using Class 3 voluntary contributions`, 
      value: `€${format(A8)}`, 
      explanation: (
        <span>
          This is a more expensive contribution class, but it is still an investment.<br/><br/>
            <a 
                href="https://nationalpensionhelpline.ie/pensions/uk-state-pension-buyback-guide-from-ireland/#contribution-classes" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ color: '#0645AD', textDecoration: 'underline' }}
            >
                Learn more about “Contribution Classes” here. 
            </a>
        </span>
      )
    },
    { 
      name: 'Your additional annual UK State Pension entitlement', 
      value: `€${format(A9)}`, 
      explanation: (
        <span>
          This is how much your purchased “qualifying years” will provide you per year in income. This income can be drawn from age 67 as per the UK pension guidelines. 
        </span>
      )
    },
    { 
      name: 'Your total annual UK State Pension entitlement', 
      value: `€${format(A10)}`, 
      explanation: (
        <span>
          This is your total annual UK state pension payment value, including both your earned and your purchased years.
        </span>
      )
    },
    { 
      name: `If you enjoy ${retirementYears} years of retirement you'll earn`, 
      value: `€${format(A11)}`, 
      explanation: (
        <span>
          Please note, under the <a 
                href="https://nationalpensionhelpline.ie/pensions/uk-state-pension-buyback-guide-from-ireland/#triple-lock" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ color: '#0645AD', textDecoration: 'underline' }}>
                    UK's triple-lock state pension provision</a>, the value of your annual UK State Pension will increase by a minimum of 2.5% each year. <br/>
                    Future State Pension increases have not been accounted for. All results are based on today's figures. 
        </span>
      )
    }
];

const message = (
    <span>
      That's an ROI of <strong>{format(ROI_Class2)}%</strong> at Class 2 contributions and <strong>{format(ROI_Class3)}%</strong> at Class 3 contributions.
    </span>
  );

return {
    status: `€${format(A11)}`,
    items,
    message: message,
    title: "Lifetime Earnings",
    table
};
}
